<template>
  <div id="resendConfirmationModal">
    <SlotModal
      id="confirmResendingModal"
      :display="state === STATE.CONFIRM_RESENDING"
      @close="$emit('close')"
    >
      <template #header>
        <span v-if="!ui.error.code">Send Confirmation Email</span>
        <span v-else>Problem Sending</span>
      </template>
      <TextInput
        v-model="confirmationEmailAddress"
        placeholder="Type email"
        id="resend-confirmation-email-input"
      />
      <ErrorDisplay v-bind="ui.error" class="mb-8" />
      <div class="mb-2 flex justify-between">
        <ButtonDefault
          id="resend-confirmation-email-no"
          label="CANCEL"
          alt
          @click="$emit('close')"
        />
        <ButtonDefault
          id="resend-confirmation-email-yes"
          label="SEND"
          @click="resendConfirmationEmail"
        />
      </div>
    </SlotModal>

    <SlotModal id="resendingEmailModal" :display="state === STATE.RESENDING_EMAIL">
      <template #header>Resend Confirmation Email</template>
      <div id="resending-email-message" class="mb-3">
        Sending email to {{ confirmationEmailAddress }}...
      </div>
      <LoaderLine id="loader-line" class="mb-16" />
    </SlotModal>

    <SlotModal id="emailResentModal" :display="state === STATE.EMAIL_RESENT" @close="done">
      <template #header>Email Sent</template>
      <div class="mb-10" id="email-sent-message">
        A confirmation email has been sent to {{ confirmationEmailAddress }}.
      </div>
      <div class="mb-2 flex justify-center">
        <ButtonDefault id="resend-email-done" label="DONE" @click="done" />
      </div>
    </SlotModal>
  </div>
</template>

<script lang="ts">
import { mapStores } from 'pinia'
import { PropType, defineComponent } from 'vue'

import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ErrorDisplay from '@/common/components/atoms/ErrorDisplay.vue'
import LoaderLine from '@/common/components/atoms/LoaderLine.vue'
import TextInput from '@/common/components/molecules/TextInput.vue'
import SlotModal from '@/common/components/organisms/SlotModal.vue'
import { useReservationStore } from '@/modules/reservation/stores/ReservationStore'
import { blankError, clearError, displayHandled } from '@/utils/errors'

import { ReservationDetailedRead } from '@generated/types'

const STATE = {
  CONFIRM_RESENDING: 'confirm-resending',
  RESENDING_EMAIL: 'resending-email',
  EMAIL_RESENT: 'email-resent',
}

export default defineComponent({
  name: 'ResendConfirmationModal',
  components: { TextInput, LoaderLine, SlotModal, ErrorDisplay, ButtonDefault },
  props: {
    reservation: {
      type: Object as PropType<ReservationDetailedRead>,
      required: true,
    },
  },
  data() {
    return {
      ui: {
        error: blankError(),
      },
      state: STATE.CONFIRM_RESENDING,
      STATE,
      confirmationEmailAddress: '',
    }
  },
  emits: {
    close: () => true,
  },
  mounted() {
    if (!this.reservation.contact?.email || this.reservation.contact.email.includes('anonymous+')) {
      this.confirmationEmailAddress = ''
    } else this.confirmationEmailAddress = this.reservation.contact.email
  },
  computed: {
    ...mapStores(useReservationStore),
  },
  methods: {
    async resendConfirmationEmail() {
      clearError(this.ui.error)
      if (!this.confirmationEmailAddress) {
        this.ui.error.message = 'Please enter an email address.'
        return
      }

      this.state = STATE.RESENDING_EMAIL
      try {
        await this.reservationStore.resendConfirmationEmail(
          this.reservation.id,
          this.confirmationEmailAddress,
        )
        this.state = STATE.EMAIL_RESENT
      } catch (error) {
        displayHandled(error, this.ui.error)
        this.state = STATE.CONFIRM_RESENDING
      }
    },
    done() {
      this.$emit('close')
      this.state = STATE.CONFIRM_RESENDING
    },
  },
})
</script>
